import { useMemo, useState } from "react";
import { ShieldCheck, RefreshCw } from "lucide-react";

interface MathCaptchaProps {
  /** Called when the user submits the correct answer. */
  onVerified: () => void;
  /** Optional cancel handler — hides the challenge. */
  onCancel?: () => void;
}

const newChallenge = () => {
  const a = 2 + Math.floor(Math.random() * 8); // 2..9
  const b = 2 + Math.floor(Math.random() * 8); // 2..9
  // Mix +/- so it's not always the same operation.
  const op = Math.random() < 0.5 ? "+" : "−";
  const answer = op === "+" ? a + b : a - b;
  return { a, b, op, answer };
};

/**
 * Inline math CAPTCHA shown only after suspicious behavior is detected.
 * Solving it calls onVerified(), which the parent uses to grant a 5-min trust window.
 */
export const MathCaptcha = ({ onVerified, onCancel }: MathCaptchaProps) => {
  const [challenge, setChallenge] = useState(newChallenge);
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  const expected = useMemo(() => challenge.answer, [challenge]);

  const refresh = () => {
    setChallenge(newChallenge());
    setValue("");
    setError(false);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (Number(value) === expected) {
      onVerified();
    } else {
      setError(true);
      // Rotate the challenge after a wrong answer to defeat brute-force.
      setTimeout(refresh, 500);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Verification challenge"
      className="mt-3 rounded-2xl border border-border bg-card p-4 shadow-2xl"
      style={{ boxShadow: "0 0 0 1px hsl(var(--primary) / 0.4), 0 8px 32px hsl(0 0% 0% / 0.6)" }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ background: "var(--hero-gradient)" }}
        >
          <ShieldCheck className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-foreground">Quick check — are you human?</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            We noticed unusual activity. Please solve this to continue.
          </p>

          <form onSubmit={submit} className="mt-3 flex items-center gap-2">
            <span className="rounded-lg bg-secondary px-3 py-2 font-mono text-base font-bold text-foreground">
              {challenge.a} {challenge.op} {challenge.b} =
            </span>
            <input
              type="number"
              inputMode="numeric"
              autoFocus
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(false);
              }}
              className="w-20 rounded-lg border border-border bg-background px-3 py-2 text-center font-mono text-base text-foreground focus:border-primary focus:outline-none"
              aria-label="Your answer"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ background: "var(--hero-gradient)" }}
            >
              Verify
            </button>
            <button
              type="button"
              onClick={refresh}
              className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary"
              aria-label="New challenge"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </form>

          {error && (
            <p className="mt-2 text-xs font-medium" style={{ color: "hsl(0 90% 65%)" }}>
              Incorrect — try the new challenge.
            </p>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="mt-2 text-[11px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
